/**
 * Page-unload (`beforeunload` / `pagehide`) browser-lifecycle surface.
 *
 * A cross-layer infrastructure utility — sibling to `eventBus.ts` and
 * `secureStorageEventBridge.ts` — that owns the page-unload
 * `window.addEventListener` registrations. It lives in `infrastructure/` (not
 * `presentation/`) so it is importable by BOTH the domain layer (e.g.
 * `analyticsService`, which flushes on unload and may not import presentation
 * under Clean Architecture's inward-only dependency rule) AND the presentation
 * layer (e.g. `videoManagementSlice`, the last-seen listener) without crossing
 * an inward-only arrow. The data layer uses it too
 * (`data/services/remoteConfig/remoteConfigDataService.ts`).
 *
 * Two events, two jobs — pick per use case:
 * - `subscribeToBeforeUnload` for a last-chance **side effect** (flush a buffer,
 *   pause media). It is the earliest unload hook, but Safari/iOS fires it
 *   unreliably.
 * - `subscribeToPageHide` for "this document is going away" **detection**.
 *   `pagehide` fires on every unload path Safari/iOS included, and also when
 *   the document enters the back/forward cache — so a consumer that keeps state
 *   across the event must tolerate the document coming back alive
 *   (`pageshow`/visible) afterwards.
 *
 * The presentation-only `useBeforeUnload` React-hook wrapper lives in
 * `presentation/utils/tabVisibility.ts` and delegates to this utility.
 *
 * Mirrors the shape and SSR guard of `subscribeToTabVisibility`
 * (`presentation/utils/tabVisibility.ts`).
 *
 * SSR-defensive: if `window` is undefined, both subscribe functions return a
 * no-op unsubscribe and skip listener registration.
 */

/**
 * Subscribe to the page-unload (`beforeunload`) event outside of React.
 *
 * @param onUnload - Invoked when the page is about to unload (tab closing,
 * reload, navigation away). Use for last-chance side effects such as flushing
 * a buffer or pausing media. Keep the work synchronous and fast — the browser
 * may not run async continuations during unload.
 * @returns An unsubscribe function that removes the underlying event listener.
 */
export function subscribeToBeforeUnload(onUnload: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleBeforeUnload = () => {
    onUnload();
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}

/**
 * Subscribe to the page-hide (`pagehide`) event outside of React.
 *
 * Unlike `beforeunload`, `pagehide` fires on **every** path that takes the
 * document away — including Safari/iOS, where `beforeunload` is unreliable —
 * which makes it the dependable signal for "this document is being torn down".
 *
 * @param onPageHide - Invoked when the document is being unloaded OR put into
 * the back/forward cache. The back/forward case can be reversed (the document
 * is later restored and keeps running), so consumers that latch state here must
 * not treat the event as permanent.
 * @returns An unsubscribe function that removes the underlying event listener.
 */
export function subscribeToPageHide(onPageHide: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handlePageHide = () => {
    onPageHide();
  };

  window.addEventListener('pagehide', handlePageHide);

  return () => {
    window.removeEventListener('pagehide', handlePageHide);
  };
}
